import { onUnmounted, ref, watch } from 'vue';

function productHref(productId) {
  return `/index.php?route=product/product&product_id=${encodeURIComponent(productId)}`;
}

function normalizeItem(item) {
  const productId = item && item.product_id != null ? String(item.product_id) : '';
  const label = String((item && (item.label || item.value)) || '');
  return {
    label,
    value: String((item && (item.value || item.label)) || ''),
    product_id: productId,
    href: (item && item.href) || (productId ? productHref(productId) : ''),
    thumb: (item && item.thumb) || '',
    price: (item && item.price) || ''
  };
}

export function searchResultsHref(term) {
  return `/index.php?route=product/search&search=${encodeURIComponent(term)}`;
}

/**
 * Debounced product-suggesties via product/search/autocomplete.
 */
export function useProductAutocomplete(queryRef, options = {}) {
  const url = options.url || '/index.php?route=product/search/autocomplete';
  const minLength = options.minLength ?? 1;
  const delay = options.delay ?? 200;
  const limit = options.limit ?? 8;

  const items = ref([]);
  const loading = ref(false);
  const open = ref(false);
  let timer = null;
  let abort = null;
  let requestId = 0;

  async function fetchItems(term) {
    if (abort) abort.abort();
    abort = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const current = ++requestId;
    loading.value = true;
    try {
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      const endpoint = `${url}${sep}term=${encodeURIComponent(term)}`;
      const res = await fetch(endpoint, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: abort ? abort.signal : undefined
      });
      if (!res.ok) throw new Error('autocomplete failed');
      const data = await res.json();
      if (current !== requestId) return;
      const list = Array.isArray(data) ? data : [];
      items.value = list.slice(0, limit).map(normalizeItem);
      open.value = true;
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      if (current !== requestId) return;
      items.value = [];
      open.value = true;
    } finally {
      if (current === requestId) loading.value = false;
    }
  }

  watch(queryRef, (value) => {
    clearTimeout(timer);
    const term = String(value || '').trim();
    if (term.length < minLength) {
      items.value = [];
      open.value = false;
      loading.value = false;
      return;
    }
    timer = setTimeout(() => fetchItems(term), delay);
  });

  onUnmounted(() => {
    clearTimeout(timer);
    if (abort) abort.abort();
  });

  return { items, loading, open, fetchItems };
}
